import { SyntheticEvent, useEffect, useState } from "react";

import { sendPasswordResetRequest } from "@/api/auth";
import { InboxIcon } from "@/assets/images";
import { Button } from "@/components/ui";
import { AuthCard } from "@/components/ui/AuthCard";
import { FormHeader } from "@/components/ui/FormHeader";
import { FRONT_DOOR_INPUT, Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { TextLink } from "@/components/ui/TextLink";

function validateEmail(email: string) {
  // prettier-ignore
  // eslint-disable-next-line no-useless-escape
  const re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(String(email).toLowerCase());
}

export const PasswordResetForm = () => {
  // Extract redirect URL from query params
  const parsedQueryString = Object.fromEntries(new URLSearchParams(window.location.search));
  const redirectUrl = parsedQueryString.redirect_url as string;

  //fields
  const [email, setEmail] = useState("");

  const [linkSent, setLinkSent] = useState(false);

  // Load email from sessionStorage after component mounts
  useEffect(() => {
    const storedEmail = sessionStorage.getItem("password-reset-email") || "";
    if (storedEmail) {
      setEmail(storedEmail);
    }
  }, []);

  function handleSubmit(event: SyntheticEvent) {
    event.preventDefault();
    // Clear the stored email after using it
    sessionStorage.removeItem("password-reset-email");
    sendPasswordResetRequest(email, redirectUrl).then((r) => setLinkSent(true));
  }

  return (
    <>
      {!linkSent && (
        <AuthCard as="form" onSubmit={handleSubmit}>
          <FormHeader
            title="Forgot your password?"
            subtitle="Nothing to worry about. We will send a unique link to your inbox. Follow the link to create yourself a new shiny password."
          />

          <div className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-semibold">
                E-mail
              </Label>
              <Input
                id="email"
                autoFocus
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={FRONT_DOOR_INPUT}
                placeholder="your.email@example.com"
              />
            </div>

            <Button
              disabled={!validateEmail(email)}
              type="submit"
              variant="brand"
              size="xl"
              className="w-full"
            >
              Reset password
            </Button>

            <div className="text-center">
              <TextLink href="/login">Back to log in</TextLink>
            </div>
          </div>
        </AuthCard>
      )}
      {linkSent && (
        <AuthCard centered>
          <div className="flex justify-center mb-8">
            <InboxIcon width={200} />
          </div>

          <div className="space-y-4">
            <h2 className="text-xl md:text-2xl font-bold tracking-tight">
              If your email address is valid, you'll receive an email with a link to reset your
              password shortly.
            </h2>

            <div className="space-y-2 pt-4">
              <h3 className="text-lg font-semibold">What's next</h3>
              <p className="text-muted-foreground">
                Check your inbox and use link to set a new password.
              </p>
            </div>
          </div>
        </AuthCard>
      )}
    </>
  );
};
